import { trackEvent } from 'utils/events/trackEvent'
import configuration from './configuration'
import { errors } from 'utils/error-handling/errors'

async function getConfig() {
  const {
    configuration: { knowledgeOwl },
  } = await configuration()
  return knowledgeOwl
}

// When KnowledgeOwl is initialized, it will attach an onclick event to the element with this ID.
// This must match the Element ID defined in the Widget Settings in KnowledgeOwl.
// Note that we create this element in the `initialize` function below.
const TRIGGER_LINK_ID = 'knowledge-owl-trigger'

// This wrapper element is added by the KnowledgeOwl embed.
// Note that it could change in theory, so do not assume that it will always exist.
const WIDGET_WRAPPER_ID = '_ko16-widget-wrapper'

// This is the property that KnowledgeOwl requires us to push our config to.
export const KO_WINDOW_PROP = '_ko16_p'

export interface WindowKnowledgeOwl extends Window {
  _ko16_p: Array<Array<string>>
}

let initialized = false

// Adapted from KnowledgeOwl Embed Code snippet
const initializeKnowledgeOwl = async () => {
  if (initialized) return

  let config
  try {
    config = await getConfig()
  } catch (e) {
    if (e && e.message) {
      errors.captureException(e, 'Error loading KnowledgeOwl configuration.')
    }
    return
  }

  const windowKO = window as unknown as WindowKnowledgeOwl

  windowKO[KO_WINDOW_PROP] = windowKO[KO_WINDOW_PROP] || []
  windowKO[KO_WINDOW_PROP].push(['_setProject', config.projectId])
  /** Oauth Token * */
  windowKO[KO_WINDOW_PROP].push(['_setToken', config.oauthToken])
  /** Optional Custom Variables * */
  // _ko16_p.push(['_setCustomVar', 'name', 'value']);

  // Add the trigger element to the DOM, so that the click event can be attached.
  // We will proxy clicks to this element from the nav
  const koTrigger = document.createElement('div')
  koTrigger.setAttribute('data-testid', 'ko-trigger')
  koTrigger.id = TRIGGER_LINK_ID
  document.body.appendChild(koTrigger)

  const ko = document.createElement('script')
  ko.setAttribute('data-testid', 'ko-script')
  ko.type = 'text/javascript'
  ko.async = true
  ko.src = `//${config.domain}/javascript/ko-index?__pc=${config.projectId}`
  document.head.appendChild(ko)

  initialized = true
}

// Check that the widget wrapper has been added to the DOM, which indicates the script has successfully loaded.
// We only warn if it is not present, as the widget wrapper ID could change.
const verifyKnowledgeOwlLoaded = () => {
  const el = document.getElementById(WIDGET_WRAPPER_ID)
  if (!el) {
    errors.captureMessage(
      'KnowledgeOwl widget wrapper element not found. May be a problem loading KnowledgeOwl.',
      { level: 'warning' }
    )
  }
}

const showKnowledgeOwl = () => {
  verifyKnowledgeOwlLoaded()

  const el = document.getElementById(TRIGGER_LINK_ID)
  if (el) {
    el.click()
    trackEvent({ id: 'help.viewed', description: 'Viewed the KnowledgeOwl help popover.' })
  } else {
    errors.captureMessage('KnowledgeOwl trigger element is missing.')
  }
}

const knowledgeOwl = { initializeKnowledgeOwl, showKnowledgeOwl }

export default knowledgeOwl
